import React, { useState, useEffect } from 'react';
import { Box, VStack, useBreakpointValue } from '@chakra-ui/react';
import ParkingDashboard from './components/ParkingDashboard';
import FAQPage from './components/FAQPage';
// Navigation removed — Home/FAQ tabs now live inside the profile menu
import Header from './components/Header';
import UpdateNotification from './components/UpdateNotification';
import { CopyrightText } from './components/CopyrightText';
import DesktopSidePanel from './components/DesktopSidePanel';
import IPhoneFrame from './components/IPhoneFrame';
import { usePlatformDetection, getLayoutBottomPadding } from './utils/platformDetection';
import { LanguageProvider } from './i18n/LanguageContext';
import { WeatherRegionProvider } from './hooks/useWeatherRegion';

const DEFAULT_PROFILES = ['Car Driver 1', 'Car Driver 2', 'Car Driver 3', 'Car Driver 4'];
const SELECTED_PROFILE_KEY = 'myCarParkedWhereSelectedProfile';
const CUSTOM_PROFILES_KEY = 'myCarParkedWhereCustomProfiles';

// Old default sets that should be replaced on upgrade
const LEGACY_PROFILE_SETS = [
  ['User1', 'User2', 'User3', 'KaiJian'],
  ['Car User', 'User2', 'User3', 'KaiJian'],
  ['Car Driver 1', 'Car Driver 2', 'Car Driver 3', 'KaiJian'],
];

function migrateProfilesIfNeeded() {
  const stored = localStorage.getItem(CUSTOM_PROFILES_KEY);
  if (!stored) return;
  try {
    const parsed: string[] = JSON.parse(stored);
    const isLegacy = LEGACY_PROFILE_SETS.some(
      set => set.length === parsed.length && set.every((p, i) => p === parsed[i])
    );
    if (isLegacy) {
      localStorage.setItem(CUSTOM_PROFILES_KEY, JSON.stringify(DEFAULT_PROFILES));
      localStorage.removeItem(SELECTED_PROFILE_KEY);
    }
  } catch {}
}
migrateProfilesIfNeeded();

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'faq'>(() => {
    const tempTabLocal = localStorage.getItem('tempActiveTab');
    const tempTabSession = sessionStorage.getItem('tempActiveTab');
    const urlHash = window.location.hash;
    const tempTab = tempTabLocal || tempTabSession || (urlHash === '#faq' ? 'faq' : null);
    if (tempTab === 'faq' || tempTab === 'dashboard') {
      localStorage.removeItem('tempActiveTab');
      sessionStorage.removeItem('tempActiveTab');
      if (window.location.hash === '#faq') window.location.hash = '';
      return tempTab as 'dashboard' | 'faq';
    }
    return 'dashboard';
  });

  const [currentProfile, setCurrentProfile] = useState(() => {
    const savedProfile = localStorage.getItem(SELECTED_PROFILE_KEY);
    const currentProfiles = localStorage.getItem(CUSTOM_PROFILES_KEY);
    const availableProfiles = currentProfiles ? JSON.parse(currentProfiles) : DEFAULT_PROFILES;
    return savedProfile && availableProfiles.includes(savedProfile) ? savedProfile : availableProfiles[0];
  });

  const platformInfo = usePlatformDetection();
  const isDesktop = useBreakpointValue({ base: false, lg: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleProfileChange = (profile: string) => {
    setCurrentProfile(profile);
    localStorage.setItem(SELECTED_PROFILE_KEY, profile);
  };

  // The raw app content — rendered identically inside the iPhone frame (desktop)
  // and as a plain column (mobile).
  const appContent =
    activeTab === 'dashboard' ? (
      <VStack spacing={0} align="stretch" w="100%">
        <Header currentProfile={currentProfile} onProfileChange={handleProfileChange} activeTab={activeTab} onTabChange={setActiveTab} />
        <UpdateNotification />
        <ParkingDashboard currentProfile={currentProfile} onProfileChange={handleProfileChange} />
        <Box px={5} py={4}>
          <CopyrightText />
        </Box>
      </VStack>
    ) : (
      <FAQPage onBackToHome={() => setActiveTab('dashboard')} currentTab={activeTab} />
    );

  const appColumn = isDesktop ? (
    <IPhoneFrame>{appContent}</IPhoneFrame>
  ) : (
    <Box
      position="relative"
      zIndex={1}
      minH="100vh"
      w="100%"
      maxW="430px"
      mx="auto"
      overflowX="hidden"
      pb={getLayoutBottomPadding(platformInfo)}
    >
      {appContent}
    </Box>
  );

  return (
    <LanguageProvider>
      <WeatherRegionProvider>
        {/* ── Desktop: side panel + phone-framed app ── */}
        {/* ── Mobile: full-width app only ── */}
        {isDesktop ? (
          <Box
            position="relative"
            zIndex={1}
            minH="100vh"
            w="100%"
            maxW="1140px"
            mx="auto"
            px={8}
            display="grid"
            gridTemplateColumns="460px 380px"
            columnGap="90px"
            alignItems="center"
            justifyContent="center"
          >
            <DesktopSidePanel />
            {appColumn}
          </Box>
        ) : (
          appColumn
        )}
      </WeatherRegionProvider>
    </LanguageProvider>
  );
};

export default App;
